import React, { useEffect, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../UI";
import AgGridWrapper from "../UI/AgGrid";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import CreateContentfulModal from "../ExperienceCode/CreateContentfulModal";
import { toast } from "react-toastify";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import ConfirmModal from "../UI/Modal/confirmModal";
import Loader from "../UI/Loader/Loader";
import { isValidJsonString } from "../../utils/helper";

const UpdateEntryAssociationModal = ({
  isOpen,
  onClose,
  associatedExperiences = [], // fetched from API
  onSaveAsNewOffer,
  onUpdateEntry,
  onUpdateAssociatedExperience,
  onDelinkExperience,
  loading : contentfulLoading,
  selectedTemplate,
  experienceJson
}) => {
  const [activations, setActivations] = useState([]);
  const [contentfulModal, setContentfulModal] = useState(false);
  const [showDelinkConfirmModal, setShowDelinkConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { content } = useContentfulContext();
  const text = content?.CONTENTFUL_ENTRY_DETAILS_CONTENT?.update_modal || {};

  const columnDefs = [
    { headerName: text.column_headers?.experience_name || "Experience Name", field: "name", flex: 1 },
    { headerName: text.column_headers?.experience_id || "Experience Id", field: "id", flex: 1 },
  ];

  useEffect(() => {
    if (!associatedExperiences || associatedExperiences.length === 0) {
      setActivations([]);
      return;
    }

    const formatted = associatedExperiences.map((exp) => ({
      id: exp.experience_id,
      name: exp.experience_name,
    }));

    setActivations(formatted);
  }, [associatedExperiences]);


  const handleSaveAsNew = async (contentType) => {
    try {
      setLoading(true);
      setError("");
      const result = await onSaveAsNewOffer(contentType);

      if (result.status) {
        toast.success(result?.message || text.success_messages?.create_entry_success || "Contentful entry created successfully");
        setContentfulModal(false);
      } else {
        // In case the API returns a failed status without throwing
        throw new Error(result?.message || text.error_messages?.create_entry_failed || "Failed to create new entry");
      }
    } catch (err) {
      setError(err?.message || text.error_messages?.create_entry_failed || "Failed to create new entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PrimaryModal isOpen={isOpen} hideModal={onClose} title={text.title || "Update Entry"} widthClass="min-w-max">
      { contentfulLoading && <Loader/> }
      <div className="p-6 space-y-6">
        <p className="text-sm font-medium text-gray-700">
          {text.configure_text || "This entry is associated with the following experiences:"}
        </p>

        {activations.length > 0 ? (
          <div style={{ height: "280px", overflow: "auto", width: "100%" }}>
            <AgGridWrapper
              rowData={activations}
              columnDefs={columnDefs}
              domLayout="autoHeight"
            />
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            {text.no_associations_text || "No associated experiences found."}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <SecondaryButton handleClick={onClose}>{text.buttons?.cancel || "Cancel"}</SecondaryButton>

          <PrimaryButton handleClick={() => setContentfulModal(true)}>
            {text.buttons?.save_as_new_entry || "Save as New Entry"}
          </PrimaryButton>

          {activations.length === 0 && (
            <PrimaryButton
              handleClick={() => {
                if (onUpdateEntry) onUpdateEntry();
                onClose();
              }}
            >
              {text.buttons?.update_entry || "Update Entry"}
            </PrimaryButton>
          )}

          {activations.length > 0 && (
            <>
              <PrimaryButton
                handleClick={() => {
                  if (onUpdateAssociatedExperience)
                    onUpdateAssociatedExperience();
                  onClose();
                }}
              >
                {text.buttons?.update_associated_experience || "Update Associated Experience"}
              </PrimaryButton>

              <PrimaryButton
                handleClick={() => setShowDelinkConfirmModal(true)}
              >
                {text.buttons?.delink_experience || "Delink Experience"}
              </PrimaryButton>
            </>
          )}
        </div>
      </div>

      {contentfulModal && (
        <CreateContentfulModal
          closeModal={() => {
            setContentfulModal(false)
            setError("")
          }}
          loading={loading}
          handleSubmit={handleSaveAsNew}
          error={error}
          selectedTemplate={selectedTemplate}
          experienceJson={typeof experienceJson === 'object' && experienceJson !== null ? experienceJson : (typeof experienceJson === 'string' && isValidJsonString(experienceJson) ? JSON.parse(experienceJson) : null)}
        />
      )}

      {showDelinkConfirmModal && (
        <ConfirmModal
          isOpen={showDelinkConfirmModal}
          hideModal={() => setShowDelinkConfirmModal(false)}
          title="Confirm Delink"
          description="Are you sure you want to delink this entry from all associated experiences? This also updates the entry."
          ConfirmText="Yes, Delink"
          CancelText="Cancel"
          loading={false}
          handleConfirmation={async () => {
            setShowDelinkConfirmModal(false);
            if (onDelinkExperience) await onDelinkExperience();
            onClose();
          }}
        />
      )}
    </PrimaryModal>
  );
};

export default UpdateEntryAssociationModal;
